// Turning validated rows into API payloads and sending them.
//
// One submitter per model. Each takes rows that have already passed validation, so it
// can rely on `resolved` being populated and does not re-derive anything.
import {
    BudgetModel,
    BudgetPricingComponentModel,
    BudgetPricingStrategyModel,
    CommodityModel,
    ComponentType,
    CounterpartyModel,
    Currency,
    DeliveryModel,
    LocationModel,
    OptionStyle,
    OptionType,
    PortfolioModel,
    PositionType,
    Side,
    Unit,
    UpdateBudgetModel,
    UpdateBudgetPriceModel,
    UpdateDealTicketRequest,
    UpdatePositionModel,
} from '../../../../Generated/Commodity-Management-Api';
import { CommodityManagementApi } from '../../../../Apis/Apis';
import { monthToNumber } from '../../../../Helpers';
import { ImportContext, ValidatedImportRow } from './ImportRow';

export interface ImportResult {
    successCount: number;
    errorCount: number;
    /** Human-readable failures, for surfacing in the chat. */
    errors: string[];
}

function monthNumber(value: unknown): number | undefined {
    if (value === undefined || value === null || value === '') return undefined;
    if (typeof value === 'number') return value;
    const parsed = monthToNumber(String(value));
    return parsed || undefined;
}

function contractDate(month: unknown, year: unknown) {
    const m = monthNumber(month);
    const y = typeof year === 'number' ? year : Number(year);
    if (!m || !y || isNaN(y)) return undefined;
    return { month: m, year: y };
}

// --------------------------------------------------------------------------
// Positions -> addTrade, one call per deal ticket.
//
// tradeDate / commodity / counterparty / portfolio / ticket number live on the TICKET,
// everything else on the position, so flat rows are grouped first. Rows with no ticket
// number get a synthetic key per counterparty+trade date rather than being dropped -
// the legacy UploadPositions page silently discards them.
// --------------------------------------------------------------------------
export async function submitPositions(
    validatedRows: ValidatedImportRow[],
    _ctx: ImportContext
): Promise<ImportResult> {
    const ticketGroups = new Map<string, ValidatedImportRow[]>();

    for (const validated of validatedRows) {
        const counterparty = validated.resolved.counterparty as CounterpartyModel | undefined;
        const tradeDate = validated.resolved.tradeDate as string | undefined;
        const explicitTicket = validated.row.values.dealTicketNumber;
        const key = explicitTicket
            ? String(explicitTicket)
            : `auto_${counterparty?.counterpartyId ?? 'none'}_${tradeDate ?? 'none'}`;

        const group = ticketGroups.get(key);
        if (group) group.push(validated);
        else ticketGroups.set(key, [validated]);
    }

    let successCount = 0;
    let errorCount = 0;
    const errors: string[] = [];

    for (const [ticketKey, group] of Array.from(ticketGroups.entries())) {
        const first = group[0];
        const commodity = first.resolved.commodity as CommodityModel | undefined;
        const counterparty = first.resolved.counterparty as CounterpartyModel | undefined;
        const portfolio = first.resolved.portfolio as PortfolioModel | undefined;

        const positions: UpdatePositionModel[] = group.map(validated => {
            const location = validated.resolved.location as LocationModel | undefined;
            return {
                locationId: location?.locationId,
                budgetDate: contractDate(validated.row.values.budgetMonth, validated.resolved.budgetYear),
                contractDate: contractDate(validated.row.values.contractMonth, validated.resolved.contractYear),
                price: (validated.resolved.price as number) ?? 0,
                volume: validated.resolved.volume as number,
                positionType: validated.resolved.positionType as PositionType | undefined,
                optionStrike: validated.resolved.optionStrike as number | undefined,
                optionType: validated.resolved.optionType as OptionType | undefined,
                optionStyle: validated.resolved.optionStyle as OptionStyle | undefined,
                side: validated.resolved.side as Side,
                unit: validated.resolved.unit as Unit,
                currency: validated.resolved.currency as Currency,
            };
        });

        const request: UpdateDealTicketRequest = {
            tradeDate: first.resolved.tradeDate as string,
            commodityId: commodity!.commodityId,
            counterpartyId: counterparty?.counterpartyId,
            portfolioId: portfolio?.portfolioId,
            dealTicketNumber: first.row.values.dealTicketNumber
                ? String(first.row.values.dealTicketNumber)
                : undefined,
            positions,
        };

        try {
            await CommodityManagementApi.addTrade(null, request);
            successCount += positions.length;
        } catch (error) {
            errorCount += positions.length;
            errors.push(`Deal ticket ${ticketKey}: ${(error as Error)?.message ?? 'upload failed'}`);
        }
    }

    return { successCount, errorCount, errors };
}

// --------------------------------------------------------------------------
// Budgets -> updateBudgets, a single batched call. budgetId 0 means insert.
//
// The single Price column is expanded into one UpdateBudgetPriceModel per component of
// the row's pricing strategy. Validation guarantees a strategy is resolved, so unlike
// the legacy importer there is no non-null assertion to blow up on.
// --------------------------------------------------------------------------
export async function submitBudgets(
    validatedRows: ValidatedImportRow[],
    _ctx: ImportContext
): Promise<ImportResult> {
    const budgets: UpdateBudgetModel[] = validatedRows.map(validated => {
        const commodity = validated.resolved.commodity as CommodityModel | undefined;
        const location = validated.resolved.location as LocationModel | undefined;
        const counterparty = validated.resolved.counterparty as CounterpartyModel | undefined;
        const budgetRound = validated.resolved.budgetRound as { budgetRoundId?: number } | undefined;
        const strategy = validated.resolved.pricingStrategy as BudgetPricingStrategyModel;

        const unit = validated.resolved.unit as Unit;
        const price = (validated.resolved.price as number) ?? 0;

        return {
            budgetId: 0,
            commodityId: commodity!.commodityId,
            pricingStrategyId: strategy.budgetPricingStrategyId,
            volume: validated.resolved.volume as number,
            unit,
            currency: validated.resolved.currency as Currency,
            budgetDate: contractDate(validated.row.values.budgetMonth, validated.resolved.budgetYear)!,
            contractDate: contractDate(validated.row.values.contractMonth, validated.resolved.contractYear),
            locationId: location?.locationId,
            counterpartyId: counterparty?.counterpartyId,
            budgetRoundId: budgetRound?.budgetRoundId,
            note: validated.resolved.note ? String(validated.resolved.note) : undefined,
            budgetPrices: (strategy.components ?? []).map((component: BudgetPricingComponentModel) => ({
                budgetPriceId: 0,
                componentType: component.componentType,
                unit,
                price,
            })),
        } as UpdateBudgetModel;
    });

    try {
        await CommodityManagementApi.updateBudgets(null, { budgets });
        return { successCount: budgets.length, errorCount: 0, errors: [] };
    } catch (error) {
        return {
            successCount: 0,
            errorCount: budgets.length,
            errors: [(error as Error)?.message ?? 'Budget upload failed'],
        };
    }
}

// --------------------------------------------------------------------------
// Budget prices -> updateBudgets, carrying the EXISTING budgetId so the call updates
// instead of inserting.
//
// A price row identifies a budget that is already there rather than describing a new
// one, so the budgets in range are read back first and matched on commodity, location
// and budget month/year. Everything else on the budget - volume, unit, currency,
// pricing strategy, counterparty, round - is round-tripped untouched; only budgetPrices
// changes. A price for a component the budget already has replaces it (same
// budgetPriceId, so it updates rather than duplicating); a new component is appended.
//
// Rows matching no budget are reported rather than quietly inserting a half-built
// budget, which is what budgetId 0 would do.
// --------------------------------------------------------------------------
/** Match key: commodity, location and budget month/year, per the matching rule. */
function budgetMatchKey(
    commodityId: number | undefined,
    locationId: number | null | undefined,
    month: number | undefined,
    year: number | undefined
): string {
    return `${commodityId ?? ''}|${locationId ?? ''}|${year ?? ''}-${month ?? ''}`;
}

function describeBudgetPriceRow(validated: ValidatedImportRow): string {
    const commodity = validated.resolved.commodity as CommodityModel | undefined;
    const location = validated.resolved.location as LocationModel | undefined;
    const month = monthNumber(validated.row.values.budgetMonth);
    const year = validated.resolved.budgetYear as number | undefined;

    return [
        commodity?.commodityName ?? 'unknown commodity',
        location?.locationName,
        month && year ? `${month}/${year}` : undefined,
    ].filter(Boolean).join(' · ');
}

export interface BudgetPriceUpdatePlan {
    /** Budgets to send, each carrying its full price collection. */
    budgets: UpdateBudgetModel[];
    matchedRowCount: number;
    unmatchedRowCount: number;
    errors: string[];
}

/**
 * Work out which budgets a set of price rows lands on, and what their price collections
 * become. Pure - the caller supplies the budgets it read back - so the matching and
 * merging rules can be tested without an API.
 *
 * Every existing price row is carried over, changed or not, because updateBudgets
 * REPLACES a budget's price collection rather than merging into it: sending only the
 * changed component would delete the rest.
 */
export function buildBudgetPriceUpdates(
    validatedRows: ValidatedImportRow[],
    existingBudgets: readonly BudgetModel[]
): BudgetPriceUpdatePlan {
    const errors: string[] = [];

    const budgetsByKey = new Map<string, BudgetModel[]>();
    for (const budget of existingBudgets) {
        const key = budgetMatchKey(
            budget.commodity?.commodityId,
            budget.location?.locationId,
            budget.budgetDate?.month,
            budget.budgetDate?.year
        );
        const group = budgetsByKey.get(key);
        if (group) group.push(budget);
        else budgetsByKey.set(key, [budget]);
    }

    // Several rows can target one budget - one per component type - so the price edits
    // are accumulated per budget before anything is sent.
    const pricesByBudgetId = new Map<number, { budget: BudgetModel; prices: Map<ComponentType, UpdateBudgetPriceModel> }>();
    let matchedRowCount = 0;
    let unmatchedRowCount = 0;

    for (const validated of validatedRows) {
        const commodity = validated.resolved.commodity as CommodityModel | undefined;
        const location = validated.resolved.location as LocationModel | undefined;
        const month = monthNumber(validated.row.values.budgetMonth);
        const year = validated.resolved.budgetYear as number | undefined;

        const matches = budgetsByKey.get(budgetMatchKey(commodity?.commodityId, location?.locationId, month, year)) ?? [];

        if (matches.length === 0) {
            unmatchedRowCount++;
            errors.push(`No budget found for ${describeBudgetPriceRow(validated)}`);
            continue;
        }

        matchedRowCount++;

        for (const budget of matches) {
            let entry = pricesByBudgetId.get(budget.budgetId);
            if (!entry) {
                entry = { budget, prices: new Map() };
                for (const existing of budget.budgetPrices ?? []) {
                    entry.prices.set(existing.componentType, {
                        budgetPriceId: existing.budgetPriceId,
                        componentType: existing.componentType,
                        price: existing.price,
                        unit: existing.unit,
                    });
                }
                pricesByBudgetId.set(budget.budgetId, entry);
            }

            const componentType = validated.resolved.componentType as ComponentType;
            const replaced = entry.prices.get(componentType);

            entry.prices.set(componentType, {
                // Keeping the id updates the existing component instead of adding a
                // second row of the same type.
                budgetPriceId: replaced?.budgetPriceId ?? 0,
                componentType,
                price: validated.resolved.price as number,
                unit: (validated.resolved.unit as Unit | undefined) ?? replaced?.unit ?? budget.unit,
            });
        }
    }

    const budgets: UpdateBudgetModel[] = Array.from(pricesByBudgetId.values()).map(({ budget, prices }) => ({
        budgetId: budget.budgetId,
        commodityId: budget.commodity.commodityId,
        pricingStrategyId: budget.budgetPricingStrategy.budgetPricingStrategyId,
        volume: budget.volume,
        unit: budget.unit,
        currency: budget.currency,
        budgetDate: budget.budgetDate,
        contractDate: budget.contractDate,
        locationId: budget.location?.locationId,
        counterpartyId: budget.counterparty?.counterpartyId,
        budgetRoundId: budget.budgetRoundModel?.budgetRoundId,
        note: budget.note ?? undefined,
        budgetPrices: Array.from(prices.values()),
    } as UpdateBudgetModel));

    return { budgets, matchedRowCount, unmatchedRowCount, errors };
}

export async function submitBudgetPrices(
    validatedRows: ValidatedImportRow[],
    _ctx: ImportContext
): Promise<ImportResult> {
    const months = validatedRows
        .map(v => ({
            month: monthNumber(v.row.values.budgetMonth),
            year: v.resolved.budgetYear as number | undefined,
        }))
        .filter((m): m is { month: number; year: number } => !!m.month && !!m.year)
        .map(m => ({ ...m, ordinal: m.year * 12 + m.month }))
        .sort((a, b) => a.ordinal - b.ordinal);

    if (months.length === 0) {
        return { successCount: 0, errorCount: validatedRows.length, errors: ['No budget month and year to match on'] };
    }

    const commodityIds = Array.from(new Set(
        validatedRows
            .map(v => (v.resolved.commodity as CommodityModel | undefined)?.commodityId)
            .filter((id): id is number => id !== undefined)
    ));

    const first = months[0];
    const last = months[months.length - 1];

    // Deliberately not filtered by location: a row with no location has to match a
    // budget with no location, and a location filter would drop those rows entirely.
    let existingBudgets: BudgetModel[];
    try {
        const response = await CommodityManagementApi.listBudgets(
            null,
            undefined,            // quoteDate
            first.year, first.month, undefined,
            last.year, last.month, undefined,
            undefined,            // fiscalYearsFilter
            commodityIds,
            undefined, undefined, undefined,
            false, false, false, false,
            undefined, undefined, undefined,
        );
        existingBudgets = response.data.rows ?? [];
    } catch (error) {
        return {
            successCount: 0,
            errorCount: validatedRows.length,
            errors: [`Could not read existing budgets: ${(error as Error)?.message ?? 'request failed'}`],
        };
    }

    const plan = buildBudgetPriceUpdates(validatedRows, existingBudgets);

    if (plan.budgets.length === 0) {
        return { successCount: 0, errorCount: plan.unmatchedRowCount, errors: plan.errors };
    }

    const budgets = plan.budgets;

    try {
        await CommodityManagementApi.updateBudgets(null, { budgets });
        return { successCount: plan.matchedRowCount, errorCount: plan.unmatchedRowCount, errors: plan.errors };
    } catch (error) {
        return {
            successCount: 0,
            errorCount: validatedRows.length,
            errors: [...plan.errors, (error as Error)?.message ?? 'Budget price update failed'],
        };
    }
}

// --------------------------------------------------------------------------
// Deliveries -> updateDeliveries, a single batched call. deliveryId 0 means insert.
// siteGuid comes from the active site, never from the file.
// --------------------------------------------------------------------------
export async function submitDeliveries(
    validatedRows: ValidatedImportRow[],
    ctx: ImportContext
): Promise<ImportResult> {
    const deliveries: DeliveryModel[] = validatedRows.map(validated => {
        const r = validated.resolved;
        return {
            deliveryId: 0,
            siteGuid: ctx.siteGuid ?? '',
            postingDate: r.postingDate as string,
            documentDate: r.documentDate as string | undefined,
            plant: String(r.plant ?? ''),
            movementType: r.movementType as number,
            movementTypeText: String(r.movementTypeText ?? ''),
            amountInLocation: r.amountInLocation as number | undefined,
            reasonForMovement: r.reasonForMovement as string | undefined,
            storageLocation: r.storageLocation as string | undefined,
            material: String(r.material ?? ''),
            materialDescription: String(r.materialDescription ?? ''),
            batch: (r.batch as string) ?? '',
            quantity: r.quantity as number,
            baseUnitofMeasure: String(r.baseUnitofMeasure ?? ''),
            customer: (r.customer as string) ?? '',
            order: r.order as string | undefined,
            recievingPlant: r.recievingPlant as string | undefined,
            vendor: String(r.vendor ?? ''),
            purchaseOrder: String(r.purchaseOrder ?? ''),
        } as DeliveryModel;
    });

    const aggregated = aggregateDeliveriesByOrder(deliveries);

    try {
        await CommodityManagementApi.updateDeliveries(null, { deliveries: aggregated });
        return { successCount: aggregated.length, errorCount: 0, errors: [] };
    } catch (error) {
        return {
            successCount: 0,
            errorCount: aggregated.length,
            errors: [(error as Error)?.message ?? 'Delivery upload failed'],
        };
    }
}

/**
 * Group deliveries sharing an order, summing quantity and amount and taking every other
 * field from the largest-quantity row. Ported from UploadDeliveries so the new pipeline
 * produces identical results to the existing page.
 */
export function aggregateDeliveriesByOrder(deliveries: DeliveryModel[]): DeliveryModel[] {
    const byOrder = new Map<string, DeliveryModel[]>();

    for (const delivery of deliveries) {
        const key = delivery.order ?? '';
        const group = byOrder.get(key);
        if (group) group.push(delivery);
        else byOrder.set(key, [delivery]);
    }

    const aggregated: DeliveryModel[] = [];
    byOrder.forEach(group => {
        if (group.length === 1) {
            aggregated.push(group[0]);
            return;
        }

        const base = group.reduce((max, current) =>
            (current.quantity ?? 0) > (max.quantity ?? 0) ? current : max
        );

        aggregated.push({
            ...base,
            quantity: group.reduce((sum, d) => sum + (d.quantity ?? 0), 0),
            amountInLocation: group.reduce((sum, d) => sum + (d.amountInLocation ?? 0), 0),
        });
    });

    return aggregated;
}

export type ImportSubmitter = (
    rows: ValidatedImportRow[],
    ctx: ImportContext
) => Promise<ImportResult>;

const SUBMITTERS: Record<string, ImportSubmitter> = {
    positions: submitPositions,
    budgets: submitBudgets,
    budgetPrices: submitBudgetPrices,
    deliveries: submitDeliveries,
};

export function getImportSubmitter(modelKey: string): ImportSubmitter | undefined {
    return SUBMITTERS[modelKey];
}
